<template>
  <scoped>
    <CustomerToolbar
      v-if="customerEnumerationResponse"
      v-model:value="customerInput"
      :customerOrders="customerEnumerationResponse.orders"
      :customerTypes="customerEnumerationResponse.types"
      @update="update()"
      @reset="reset()"
    />
    <CustomerList
      v-if="customerResponse"
      v-model:selectedIndex="selectedIndex"
      :items="customerResponse.records"
      @select="select()"
    />
    <Paginator
      v-if="customerResponse"
      v-model:currentPage="customerInput.plot.page"
      :itemTotal="customerResponse.recordsTotal"
      :pageItems="customerInput.plot.items"
      @update="update()"
    />
    <ErrorModal
      v-if="error"
      v-model:error="error"
    />
  </scoped>
</template>

<script setup lang="ts">
  import CustomerList from "@/components/lists/customer.vue"
  import CustomerToolbar from "@/components/toolbars/customer.vue"
  import {nextTick, onBeforeMount, PropType, ref} from "vue"
  import {
    Customer,
    CustomerEnumerationResponse,
    CustomerInput,
    CustomerResponse
  } from "@/endpoints/rutsolve/gql/generated/graphql"
  import Paginator from "@/components/paginator.vue"
  import {Customer as CustomerClient} from "@/endpoints/rutsolve/gql/resolvers/customer"
  import {Enumeration as EnumerationClient} from "@/endpoints/rutsolve/gql/resolvers/enumeration"
  import {CustomerSelector as Storage} from "@/storage/customer_selector"
  import ErrorModal from "@/components/modals/error.vue"

  const value = defineModel("value", {
    default: null,
    required: false,
    type: Object as PropType<Customer>
  })

  const emits = defineEmits([
    "select",
  ])

  const error = ref<Error>(null)
  const customerClient = new CustomerClient()
  const enumerationClient = new EnumerationClient()
  const customerResponse = ref<CustomerResponse>(null)
  const customerEnumerationResponse = ref<CustomerEnumerationResponse>(null)
  const customerInput = ref<CustomerInput>(null)
  const selectedIndex = ref<number>(null)

  onBeforeMount( () => {
    read()
  })

  function update() {
    Storage.writeCustomerInput(customerInput.value)
    read()
  }

  async function select() {
    const selectedCustomer = customerResponse.value.records[selectedIndex.value]
    value.value = selectedCustomer
    Storage.writeSelectedCustomerID(selectedCustomer.id)
    await nextTick()
    emits("select")
  }

  function reset() {
    Storage.deleteCustomerInput()
    Storage.deleteSelectedCustomerID()
    selectedIndex.value = null
    value.value = null
    read()
  }

  async function read() {
    try {
      customerEnumerationResponse.value = await enumerationClient.readCustomerEnumerations()
      const storedCustomerInput = Storage.readCustomerInput()
      customerInput.value = structuredClone(storedCustomerInput)
      customerResponse.value = await customerClient.selector(storedCustomerInput)
      if (!customerResponse.value || !customerResponse.value.records) {
        return
      }
      const storedSelectedCustomerID = Storage.readSelectedCustomerID()
      for (const [index, customer] of customerResponse.value.records.entries()) {
        if (customer.id == storedSelectedCustomerID) {
          selectedIndex.value = index
          value.value = customer
          break
        }
      }
    } catch(e) {
      error.value = e
    }
  }
</script>
